import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import {
  type DefaultError,
  keepPreviousData,
  useMutation,
  useQuery,
  type UseQueryReturnType
} from '@tanstack/vue-query';
import axiosInstance from '@/plugins/axios';
import { computed, ref } from 'vue';
import type {
  I2faGetResponse,
  IAccountPasswordRequest,
  IAspsDeleteRequest,
  IAspsListResponse,
  IAspsPostRequest,
  IAspsPostResponse,
  ISecurityEventParams,
  ISecurityEventsResponse,
  ITotpRemoveRequest,
  ITotpSetupResponse,
  ITotpSetupValidateRequest
} from '@/types/services/security.service';
import type { AxiosError } from 'axios';
import type { IDownloadSecurityEventRequest } from '@/types/views/auth/SecurityEvent';
import type {
  IMcpToken,
  IMcpTokenCreateRequest,
  IMcpTokenCreateResponse,
  IMcpTokensListResponse,
  IMcpTokensRevokeRequest,
  IMcpTokensRevokeResponse
} from '@/types/services/mcp.service';

export type IMcpTokensQueryKey = (string | null)[];

export const mcpTokensQueryKey = (userIndex: string | null): IMcpTokensQueryKey => [
  'authenticatedQuery',
  userIndex,
  'settings',
  'mcp',
  'tokens'
];

const useSecurityService = () => {
  const { userIndex, queriesEnabled } = useAuthenticatedQuery();

  const get2fa = (): UseQueryReturnType<I2faGetResponse, DefaultError> =>
    useQuery({
      queryKey: ['authenticatedQuery', userIndex, 'settings', 'security', 'get2fa'],
      queryFn: async () => {
        return (await axiosInstance.get(`/u/${userIndex.value}/auth/2fa`)).data;
      },
      enabled: queriesEnabled
    });

  const enableTotp = () => {
    return useMutation<ITotpSetupResponse>({
      mutationKey: ['authenticatedQuery', userIndex, 'settings', 'security', 'enableTotp'],
      mutationFn: async () => {
        return (
          await axiosInstance.post<ITotpSetupResponse>(`/u/${userIndex.value}/settings/2fa/setup`)
        ).data;
      }
    });
  };

  const validateTotp = () => {
    return useMutation<void, DefaultError, ITotpSetupValidateRequest>({
      mutationKey: ['authenticatedQuery', userIndex, 'settings', 'security', 'validateTotp'],
      mutationFn: async (body) => {
        await axiosInstance.post(`/u/${userIndex.value}/settings/2fa/setup/validate`, body);
      }
    });
  };

  const removeTotp = () => {
    return useMutation<void, DefaultError, ITotpRemoveRequest>({
      mutationKey: ['authenticatedQuery', userIndex, 'settings', 'security', 'removeTotp'],
      mutationFn: async (body) => {
        await axiosInstance.post(`/u/${userIndex.value}/settings/2fa/remove`, body);
      }
    });
  };

  // Application Password
  const getAsps = (): UseQueryReturnType<IAspsListResponse['results'], DefaultError> =>
    useQuery({
      queryKey: ['authenticatedQuery', userIndex, 'settings', 'security', 'getAsps'],
      queryFn: async () => {
        const asps = (
          await axiosInstance.get<IAspsListResponse>(`/u/${userIndex.value}/settings/asps`)
        ).data;
        return asps.results.reverse();
      },
      enabled: queriesEnabled
    });

  const postAsps = () => {
    return useMutation<IAspsPostResponse, DefaultError, IAspsPostRequest>({
      mutationKey: ['authenticatedQuery', userIndex, 'settings', 'security', 'postAsps'],
      mutationFn: async (body) => {
        return (
          await axiosInstance.post<IAspsPostResponse>(`/u/${userIndex.value}/settings/asps`, body)
        ).data;
      }
    });
  };

  const deleteAsps = () => {
    return useMutation<void, DefaultError, IAspsDeleteRequest>({
      mutationKey: ['authenticatedQuery', userIndex, 'settings', 'security', 'deleteAsps'],
      mutationFn: async (body) => {
        await axiosInstance.delete(`/u/${userIndex.value}/settings/asps/${body.id}`);
      }
    });
  };

  // Account Password
  const postAccountPassword = () => {
    return useMutation<void, AxiosError, IAccountPasswordRequest>({
      mutationKey: [
        'authenticatedQuery',
        userIndex,
        'settings',
        'security',
        'post',
        'accountPassword'
      ],
      mutationFn: async (body) => {
        await axiosInstance.post(`/u/${userIndex.value}/settings/password`, body);
      }
    });
  };

  // Security Events

  const eventsPaginationHelper = (newPage: number, next?: string, previous?: string) => {
    const direction = newPage > page.value ? 'next' : 'prev';
    if (next && direction === 'next') {
      eventsParams.value = { page: newPage, next };
    } else if (previous && direction === 'prev') {
      eventsParams.value = { page: newPage, previous };
    }
    page.value = newPage;
  };
  const page = ref(1);
  const eventsParams = ref<ISecurityEventParams>();

  const getEvents = (): UseQueryReturnType<ISecurityEventsResponse, DefaultError> =>
    useQuery({
      queryKey: ['authenticatedQuery', userIndex, 'settings', 'security', 'events', eventsParams],
      queryFn: async () => {
        return (
          await axiosInstance.get<ISecurityEventsResponse>(
            `/u/${userIndex.value}/settings/events`,
            {
              params: eventsParams.value
            }
          )
        ).data;
      },
      placeholderData: keepPreviousData,
      enabled: queriesEnabled,
      staleTime: 300
    });

  const downloadSecurityCSV = () => {
    return useMutation<Blob, DefaultError, IDownloadSecurityEventRequest>({
      mutationKey: [
        'authenticatedQuery',
        userIndex,
        'settings',
        'security',
        'events',
        'download',
        'csv'
      ],
      mutationFn: async (params) => {
        return (
          await axiosInstance.get(`/u/${userIndex.value}/settings/events/export/csv`, {
            responseType: 'blob',
            params
          })
        ).data;
      }
    });
  };

  const tokensQueryKey = computed(() => mcpTokensQueryKey(userIndex.value));

  const getMcpTokens = (): UseQueryReturnType<IMcpToken[], DefaultError> =>
    useQuery({
      queryKey: tokensQueryKey,
      queryFn: async () => {
        const res = (
          await axiosInstance.get<IMcpTokensListResponse>(
            `/u/${userIndex.value}/settings/mcp-tokens`
          )
        ).data;
        return res.results.sort((a, b) => (a.created < b.created ? 1 : -1));
      },
      enabled: queriesEnabled
    });

  const createMcpToken = () => {
    return useMutation<IMcpTokenCreateResponse, DefaultError, IMcpTokenCreateRequest>({
      mutationKey: ['authenticatedQuery', userIndex, 'settings', 'mcp', 'create'],
      mutationFn: async (body) => {
        return (
          await axiosInstance.post<IMcpTokenCreateResponse>(
            `/u/${userIndex.value}/settings/mcp-tokens`,
            body
          )
        ).data;
      }
    });
  };

  const revokeMcpTokens = () => {
    return useMutation<IMcpTokensRevokeResponse, DefaultError, IMcpTokensRevokeRequest>({
      mutationKey: ['authenticatedQuery', userIndex, 'settings', 'mcp', 'revoke'],
      mutationFn: async (body) => {
        return (
          await axiosInstance.delete<IMcpTokensRevokeResponse>(
            `/u/${userIndex.value}/settings/mcp-tokens`,
            { data: body }
          )
        ).data;
      }
    });
  };

  return {
    get2fa,
    enableTotp,
    validateTotp,
    removeTotp,
    getAsps,
    postAsps,
    deleteAsps,
    postAccountPassword,
    eventsPaginationHelper,
    getEvents,
    downloadSecurityCSV,
    page,
    tokensQueryKey,
    getMcpTokens,
    createMcpToken,
    revokeMcpTokens
  };
};

export default useSecurityService;
